import React, { useMemo, useRef } from 'react';
import { gsap, ScrollTrigger, useGSAP, MOTION_OK } from '../lib/gsap';

// Animação do globo: Pixabay (licença livre para uso comercial, sem crédito),
// convertida de GIF (4,5 MB) para WebP animado (~470 KB).
const BALL_SRC = '/img/disco-ball.webp';

// Pontos de luz refletidos, gerados uma vez com posições fixas (sem Math.random
// no render para não mudar a cada re-render).
const SPOT_COLORS = ['255 214 120', '255 255 255', '255 120 200', '120 200 255', '245 179 1'];
const makeSpots = (count) =>
  Array.from({ length: count }, (_, i) => {
    const angle = (i * 137.5 * Math.PI) / 180; // ângulo áureo espalha sem agrupar
    const radius = 18 + ((i * 53) % 80); // 18–98 vmax do centro do globo
    return {
      left: `calc(50% + ${Math.cos(angle) * radius}vmax)`,
      top: `calc(50% + ${Math.sin(angle) * radius}vmax)`,
      size: 10 + ((i * 7) % 22),
      color: SPOT_COLORS[i % SPOT_COLORS.length],
      delay: `${(i % 8) * 0.25}s`,
    };
  });

// Globo de discoteca atravessa o fundo conforme a página rola. Duas camadas:
// o globo fica atrás do conteúdo, transparente para não competir com o texto;
// os reflexos ficam por cima em mix-blend "screen", só clareando (nunca
// escurecem texto nem bloqueiam clique).
const DiscoBall = () => {
  const ball = useRef(null);
  const beams = useRef(null);
  const spots = useMemo(() => makeSpots(20), []);

  useGSAP(() => {
    gsap.matchMedia().add(MOTION_OK, () => {
      const w = () => window.innerWidth;
      const h = () => window.innerHeight;
      const targets = [ball.current, beams.current];

      // Trajeto em zigue-zague: direita → esquerda → direita → centro.
      gsap
        .timeline({
          defaults: { ease: 'sine.inOut' },
          scrollTrigger: { start: 0, end: 'max', scrub: 1.2, invalidateOnRefresh: true },
        })
        .fromTo(targets, { x: () => w() * 0.62, y: () => h() * 0.24 }, { x: () => w() * 0.16, y: () => h() * 0.34 })
        .to(targets, { x: () => w() * 0.82, y: () => h() * 0.24 })
        .to(targets, { x: () => w() * 0.5, y: () => h() * 0.2 });

      // Reflexos giram devagar; rolar acelera conforme a velocidade.
      const orbit = gsap.to('[data-disco-spots]', { rotation: 360, duration: 60, ease: 'none', repeat: -1 });
      ScrollTrigger.create({
        start: 0,
        end: 'max',
        onUpdate: (self) => {
          const boost = 1 + Math.min(Math.abs(self.getVelocity()) / 150, 12);
          gsap.to(orbit, { timeScale: boost, duration: 0.2, overwrite: true });
          gsap.to(orbit, { timeScale: 1, duration: 1.2, delay: 0.2, ease: 'power2.out' });
        },
      });
    });
  });

  return (
    <>
      {/* Camadas recortadas na tela: reflexos longe do globo não criam rolagem lateral. */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
        <div ref={ball} className="disco-ball-wrap absolute left-0 top-0">
          <img src={BALL_SRC} alt="" width="320" height="320" decoding="async" className="disco-ball" />
        </div>
      </div>

      <div className="pointer-events-none fixed inset-0 z-20 overflow-hidden" aria-hidden="true">
        <div ref={beams} className="disco-beams-wrap absolute left-0 top-0">
          <div data-disco-spots className="disco-spots">
            {spots.map((s, i) => (
              <span
                key={i}
                className="disco-spot"
                style={{ left: s.left, top: s.top, width: s.size, height: s.size, '--spot': s.color, animationDelay: s.delay }}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default DiscoBall;
